import type { Location } from "history";
import React from "react";
import { Container } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { getRequestInfo } from "@gamedev-web/gamedev-sdk-core";
import { BasicButton, ButtonType } from "@gaming/xds-react-js";

import { localePickerRegions } from "../../configs/localePickerConfig";
import {
  defaultLocaleReturnPath,
  defCultureCookieName,
  localePickerPath,
} from "../../constants/localePicker";
import { setCookie, usePreviousLocation } from "../../helpers";
import { useResourceStrings } from "../../localization/hooks";
import * as styles from "./LocalePickerPage.module.css";

type ReturnLocation = Pick<Location, "pathname" | "search" | "hash">;

export const getSafeReturnLocation = (returnTo: string | null): ReturnLocation | undefined => {
  if (!returnTo?.startsWith("/") || returnTo.startsWith("//")) {
    return;
  }

  const returnUrl = new URL(returnTo, "https://return.local");
  const normalizedPathname = returnUrl.pathname.replace(/\/+$/, "") || "/";
  if (normalizedPathname === localePickerPath) {
    return;
  }

  return {
    pathname: returnUrl.pathname,
    search: returnUrl.search,
    hash: returnUrl.hash,
  };
};

export const getLocaleDestination = (
  origin: string,
  locale: string,
  previousLocation?: ReturnLocation
) => {
  const url = new URL(origin);
  const previousPathname = previousLocation?.pathname;
  const normalizedPreviousPathname = previousPathname?.replace(/\/+$/, "") || "/";
  const canReturnToPreviousLocation =
    !!previousPathname && normalizedPreviousPathname !== localePickerPath;
  const returnPath = canReturnToPreviousLocation ? previousPathname : defaultLocaleReturnPath;

  url.pathname = `/${locale}${returnPath}`;
  url.search = canReturnToPreviousLocation ? previousLocation?.search ?? "" : "";
  url.hash = canReturnToPreviousLocation ? previousLocation?.hash ?? "" : "";

  return url.toString();
};

export const LocalePickerPage = () => {
  const requestOrigin = useSelector(getRequestInfo).origin;
  const location = useLocation();
  const previousLocation = usePreviousLocation().location;
  const returnLocation =
    getSafeReturnLocation(new URLSearchParams(location.search).get("returnTo")) ?? previousLocation;
  const resourceKeys = [
    "localePickerTitle",
    ...localePickerRegions.flatMap(region => [
      region.titleResourceKey,
      ...region.options.map(option => option.labelResourceKey),
    ]),
  ];
  const localizedStrings = useResourceStrings(...resourceKeys);
  const stringsByKey = new Map(
    resourceKeys.map((resourceKey, index) => [resourceKey, localizedStrings[index]])
  );

  return (
    <Container className={styles.container}>
      <section aria-labelledby="locale-picker-title" className={styles.content}>
        <h1 id="locale-picker-title" className={styles.title}>
          {stringsByKey.get("localePickerTitle")}
        </h1>
        <div className={styles.regions}>
          {localePickerRegions.map(region => {
            const headingId = `locale-picker-${region.id}`;

            return (
              <section aria-labelledby={headingId} className={styles.region} key={region.id}>
                <h2 className={styles.regionTitle} id={headingId}>
                  {stringsByKey.get(region.titleResourceKey)}
                </h2>
                <div className={styles.options}>
                  {region.options.map(option => (
                    <BasicButton
                      as="button"
                      buttonType={ButtonType.Secondary}
                      className={styles.localeButton}
                      key={option.locale}
                      lang={option.locale}
                      onClick={() => {
                        setCookie({
                          name: defCultureCookieName,
                          value: option.locale,
                          sameSite: "lax",
                        });
                        window.location.assign(
                          getLocaleDestination(requestOrigin, option.locale, returnLocation)
                        );
                      }}
                      type="button"
                    >
                      {stringsByKey.get(option.labelResourceKey)}
                    </BasicButton>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </section>
    </Container>
  );
};
